import { useEffect, useState } from "react";
import { Routes, Route, Link } from "react-router-dom";
import Home from "./pages/Home";
import Report from "./pages/Report";
import SystemHealth from "./pages/SystemHealth";
import PlatformHealth from "./pages/PlatformHealth";

interface ClientPrincipal {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
  claims?: { typ: string; val: string }[];
}

function principalEmail(p: ClientPrincipal): string {
  const claims = p.claims ?? [];
  const preferred = claims.find(c => c.typ === "preferred_username")?.val;
  const mail = claims.find(c =>
    c.typ === "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress" ||
    c.typ === "email"
  )?.val;
  return preferred ?? mail ?? p.userDetails;
}

function NotFound() {
  return (
    <main style={{ padding: "1.5rem", fontFamily: "system-ui, sans-serif", maxWidth: "1600px", margin: "0 auto" }}>
      <h1>Report not found</h1>
      <p>The report you're looking for doesn't exist or you don't have access to it.</p>
      <Link to="/" style={{ color: "#0078d4", textDecoration: "none" }}>← Back to reports</Link>
    </main>
  );
}

export default function App() {
  const [authState, setAuthState] = useState<"loading" | "anon" | ClientPrincipal>("loading");

  useEffect(() => {
    fetch("/.auth/me")
      .then(r => r.json())
      .then((data: { clientPrincipal: ClientPrincipal | null }) => {
        if (data.clientPrincipal && data.clientPrincipal.userRoles?.includes("authenticated")) {
          setAuthState(data.clientPrincipal);
        } else {
          setAuthState("anon");
        }
      })
      .catch(() => setAuthState("anon"));
  }, []);

  if (authState === "loading") {
    return (
      <div style={{ padding: "2rem", fontFamily: "system-ui, sans-serif" }}>
        <p>Checking authentication…</p>
      </div>
    );
  }

  if (authState === "anon") {
    return (
      <div style={{ padding: "2rem", fontFamily: "system-ui, sans-serif", textAlign: "center" }}>
        <h1>Prostar Analytics Portal</h1>
        <p>Sign in with your Microsoft account to continue.</p>
        <a
          href="/.auth/login/aad"
          data-testid="sign-in-btn"
          style={{
            display: "inline-block",
            padding: "0.75rem 1.5rem",
            background: "#0078d4",
            color: "white",
            textDecoration: "none",
            borderRadius: "4px",
            marginTop: "1rem"
          }}
        >
          Sign in with Microsoft
        </a>
      </div>
    );
  }

  const email = principalEmail(authState);

  return (
    <div>
      <header
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          padding: "0.5rem 1rem",
          borderBottom: "1px solid #ddd",
          fontFamily: "system-ui, sans-serif",
          fontSize: "0.85rem"
        }}
      >
        <span data-testid="auth-email" style={{ marginRight: "1rem", color: "#555" }}>
          {email}
        </span>
        <a
          href="/.auth/logout"
          data-testid="sign-out-btn"
          style={{ color: "#0078d4", textDecoration: "none" }}
        >
          Sign out
        </a>
      </header>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/reports/:slug" element={<Report />} />
        <Route path="/system-health" element={<SystemHealth />} />
        <Route path="/health" element={<PlatformHealth />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
}
